import { createSlice } from '@reduxjs/toolkit';
import {
    OrderDetailFilesFetchSuccessPayload,
    OrderDetailFilesResType,
    OrderDetailStateType,
    OrderTransportHistoryType,
} from 'src/types/orderDetail';
import { OrderType } from 'src/types/order';
import {
    applyPendingOrderFiles,
    formatClaimsReturnData,
    formatData,
    resolveFilesFetchSuccess,
    stashOrderFilesOnClear,
} from './functions';

const initialState: OrderDetailStateType = {
    data: null,
    detailFetchRevision: 0,
    changelog: null,
    expeditionPackages: null,
    transportHistory: null,
    orderTransports: null,
    claimsReturns: null,
    loading: false,
    error: false,
    orderPallets: null,
    palletTypes: null,
    pendingOrderFiles: null,
};

export const orderDetailSlice = createSlice({
    name: 'orderDetail',
    initialState,
    reducers: {
        getOrderDetailFetch: (state, action) => {
            state.loading = true;
            state.error = false;
        },
        getOrderDetailFetchSuccess: (state, action) => {
            const incoming = formatData(action.payload.data);
            const prev = state.data;
            // Main GET uses fetchFiles=false → FILES is always []; keep lazy-loaded collections.
            const merged = {
                ...incoming,
                ORDER_PRODUCTS:
                    incoming.ORDER_PRODUCTS !== undefined ? incoming.ORDER_PRODUCTS : prev?.ORDER_PRODUCTS,
                ORDER_PRODUCT_SETS:
                    incoming.ORDER_PRODUCT_SETS !== undefined
                        ? incoming.ORDER_PRODUCT_SETS
                        : prev?.ORDER_PRODUCT_SETS,
                FILES: prev?.FILES,
            };
            const withPending = applyPendingOrderFiles(merged as OrderType, state.pendingOrderFiles);
            state.data = withPending.order;
            state.pendingOrderFiles = withPending.pending;
            state.detailFetchRevision += 1;
            state.changelog = null;
            state.loading = false;
        },
        getOrderDetailFetchFailure: (state, action) => {
            state.data = null;
            state.changelog = null;
            state.loading = false;
            state.error = true;
        },
        getOrderDetailFilesFetch: (state, action) => {
            state.loading = true;
            state.error = false;
        },
        getOrderDetailFilesFetchSuccess: (state, action: { payload: OrderDetailFilesFetchSuccessPayload }) => {
            const resolved = resolveFilesFetchSuccess({
                data: state.data as OrderType | null,
                pending: state.pendingOrderFiles,
                orderId: action.payload.orderId,
                files: action.payload.FILES,
            });
            if (resolved.data) {
                state.data = resolved.data;
            }
            state.pendingOrderFiles = resolved.pending;
            state.loading = false;
        },
        getOrderDetailFilesFetchFailure: (state, _action) => {
            state.loading = false;
            state.error = true;
        },
        getOrderDetailProductsFetch: (state, _action) => {
            state.loading = true;
            state.error = false;
        },
        getOrderDetailProductsFetchSuccess: (state, action) => {
            if (state.data) {
                state.data.ORDER_PRODUCTS = action.payload.ORDER_PRODUCTS;
                state.data.ORDER_PRODUCT_SETS = action.payload.ORDER_PRODUCT_SETS;
            }
            state.loading = false;
        },
        getOrderDetailProductsFetchFailure: (state, _action) => {
            state.loading = false;
            state.error = true;
        },
        getOrderDetailProductsFilesFetch: (state, _action) => {
            state.loading = true;
            state.error = false;
        },
        getOrderDetailProductsFilesFetchSuccess: (state, action: { payload: OrderDetailFilesResType }) => {
            const files = action.payload?.FILES;
            if (files && state.data && state.data.ORDER_PRODUCTS) {
                state.data.ORDER_PRODUCTS = state.data.ORDER_PRODUCTS.map((orderProduct) => {
                    const productFiles = files
                        .filter(
                            (file) =>
                                Number(file.ORDERS_PRODUCTS_ID) === Number(orderProduct.ORDERS_PRODUCTS_ID)
                        )
                        .map((file) => file.FILE)
                        .filter((file): file is NonNullable<typeof file> => Boolean(file));
                    return { ...orderProduct, FILES: productFiles };
                });
            }

            state.loading = false;
        },
        getOrderDetailProductsFilesFetchFailure: (state, _action) => {
            state.loading = false;
            state.error = true;
        },
        getOrderDetailChangelogFetch: (state, action) => {
            state.loading = true;
            state.error = false;
        },
        getOrderDetailChangelogFetchSuccess: (state, action) => {
            state.changelog = action.payload.data;
            state.loading = false;
        },
        getOrderDetailChangelogFetchFailure: (state, action) => {
            state.changelog = null;
            state.loading = false;
            state.error = true;
        },
        getOrderDetailExpeditionPackagesFetch: (state, action) => {
            state.loading = true;
            state.error = false;
        },
        getOrderDetailExpeditionPackagesFetchSuccess: (state, action) => {
            state.expeditionPackages = action.payload.data;
            state.loading = false;
        },
        getOrderDetailExpeditionPackagesFetchFailure: (state, action) => {
            state.expeditionPackages = null;
            state.loading = false;
            state.error = true;
        },
        getOrderDetailTransportHistoryFetch: (state, action) => {
            state.loading = true;
            state.error = false;
        },
        getOrderDetailTransportHistoryFetchSuccess: (state, action) => {
            state.transportHistory = action.payload?.data.filter((item: OrderTransportHistoryType) => !!item);
            state.loading = false;
        },
        getOrderDetailTransportHistoryFetchFailure: (state, action) => {
            state.transportHistory = null;
            state.loading = false;
            state.error = true;
        },
        getOrderDetailTransportsFetch: (state, _action) => {
            state.loading = true;
            state.error = false;
        },
        getOrderDetailTransportsFetchSuccess: (state, action) => {
            state.orderTransports = action.payload.data ?? [];
            state.loading = false;
        },
        getOrderDetailTransportsFetchFailure: (state, _action) => {
            state.orderTransports = null;
            state.loading = false;
            state.error = true;
        },
        getOrderDetailClaimsReturnsFetch: (state, action) => {
            state.loading = true;
            state.error = false;
        },
        getOrderDetailClaimsReturnsFetchSuccess: (state, action) => {
            state.claimsReturns = formatClaimsReturnData(action.payload);
            state.loading = false;
        },
        getOrderDetailClaimsReturnsFetchFailure: (state, action) => {
            state.claimsReturns = null;
            state.loading = false;
            state.error = true;
        },
        createOrderDetailFetch: (state, action) => {
            state.loading = true;
            state.error = false;
        },
        createOrderDetailFetchSuccess: (state, action) => {
            const formatted = formatData(action.payload.data);
            // Create responses often include FILES: [] — omit so Transport lazy-fetch can run after navigate.
            if (!formatted.FILES?.length) {
                delete formatted.FILES;
            }
            state.data = formatted;
            state.loading = false;
            state.changelog = null;
        },
        createOrderDetailFetchFailure: (state, action) => {
            state.loading = false;
            state.error = true;
            state.changelog = null;
        },
        updateOrderDetailFetch: (state, action) => {
            state.loading = true;
            state.error = false;
        },
        updateOrderDetailFetchSuccess: (state, action) => {
            state.data = formatData(action.payload.data);
            state.loading = false;
            state.changelog = null;
        },
        updateOrderDetailFetchFailure: (state, action) => {
            state.loading = false;
            state.error = true;
            state.changelog = null;
        },
        patchOrderDetailFetch: (state, action) => {
            state.loading = true;
            state.error = false;
        },
        patchOrderDetailFetchSuccess: (state, action) => {
            state.data = formatData(action.payload.data);
            state.loading = false;
            state.changelog = null;
        },
        patchOrderDetailFetchFailure: (state, action) => {
            state.loading = false;
            state.error = true;
            state.changelog = null;
        },
        editOrderDetailEshopNoteFetch: (state, action) => {
            state.loading = true;
            state.error = false;
        },
        editOrderDetailEshopNoteFetchSuccess: (state, action) => {
            state.data = formatData(action.payload.data);
            state.loading = false;
            state.changelog = null;
        },
        editOrderDetailEshopNoteFetchFailure: (state, action) => {
            state.loading = false;
            state.error = true;
            state.changelog = null;
        },
        createInvoiceOrderDetailFetch: (state, action) => {
            state.loading = true;
            state.error = false;
        },
        createInvoiceOrderDetailFetchSuccess: (state, action) => {
            state.loading = false;
        },
        createInvoiceOrderDetailFetchFailure: (state, action) => {
            state.loading = false;
            state.error = true;
        },
        setOrderDetailData: (state, action) => {
            state.data = action.payload;
        },
        clearOrderDetail: (state) => ({
            ...initialState,
            pendingOrderFiles: stashOrderFilesOnClear(state.data as OrderType | null, state.pendingOrderFiles),
        }),
        getOrderPalletsFetch: (state, action) => {
            state.loading = true;
            state.error = false;
        },
        getOrderPalletsFetchSuccess: (state, action) => {
            state.orderPallets = action.payload.data;
            state.loading = false;
        },
        getOrderPalletsFetchFailure: (state, action) => {
            state.orderPallets = null;
            state.loading = false;
            state.error = true;
        },
        getPalletTypesFetch: (state, action) => {
            state.loading = true;
            state.error = false;
        },
        getPalletTypesFetchSuccess: (state, action) => {
            state.palletTypes = action.payload.data;
            state.loading = false;
        },
        getPalletTypesFetchFailure: (state, action) => {
            state.palletTypes = null;
            state.loading = false;
            state.error = true;
        },
    },
});

export const {
    getOrderDetailFetch,
    getOrderDetailFetchSuccess,
    getOrderDetailFetchFailure,
    getOrderDetailFilesFetch,
    getOrderDetailFilesFetchSuccess,
    getOrderDetailFilesFetchFailure,
    getOrderDetailProductsFetch,
    getOrderDetailProductsFetchSuccess,
    getOrderDetailProductsFetchFailure,
    getOrderDetailProductsFilesFetch,
    getOrderDetailProductsFilesFetchSuccess,
    getOrderDetailProductsFilesFetchFailure,
    getOrderDetailChangelogFetch,
    getOrderDetailChangelogFetchSuccess,
    getOrderDetailChangelogFetchFailure,
    getOrderDetailExpeditionPackagesFetch,
    getOrderDetailExpeditionPackagesFetchSuccess,
    getOrderDetailExpeditionPackagesFetchFailure,
    getOrderDetailTransportHistoryFetch,
    getOrderDetailTransportHistoryFetchSuccess,
    getOrderDetailTransportHistoryFetchFailure,
    getOrderDetailTransportsFetch,
    getOrderDetailTransportsFetchSuccess,
    getOrderDetailTransportsFetchFailure,
    getOrderDetailClaimsReturnsFetch,
    getOrderDetailClaimsReturnsFetchSuccess,
    getOrderDetailClaimsReturnsFetchFailure,
    createOrderDetailFetch,
    createOrderDetailFetchSuccess,
    createOrderDetailFetchFailure,
    updateOrderDetailFetch,
    updateOrderDetailFetchSuccess,
    updateOrderDetailFetchFailure,
    createInvoiceOrderDetailFetch,
    createInvoiceOrderDetailFetchSuccess,
    createInvoiceOrderDetailFetchFailure,
    patchOrderDetailFetch,
    patchOrderDetailFetchSuccess,
    patchOrderDetailFetchFailure,
    editOrderDetailEshopNoteFetch,
    editOrderDetailEshopNoteFetchSuccess,
    editOrderDetailEshopNoteFetchFailure,
    setOrderDetailData,
    clearOrderDetail,
    getOrderPalletsFetch,
    getOrderPalletsFetchSuccess,
    getOrderPalletsFetchFailure,
    getPalletTypesFetch,
    getPalletTypesFetchSuccess,
    getPalletTypesFetchFailure,
} = orderDetailSlice.actions;

export default orderDetailSlice.reducer;
